import axios from "axios";
import { db } from "../db/indexeddb";
import { API_ENDPOINT } from "../../services/api/resources";
import { shouldMakeAPICall } from "../utils/network";

const GRN_STORE = "grn_store";
const LEGACY_GRN_STORE = "grnData";
const USERS_STORE = "users_store";
const INVOICE_STORE = "invoice_store";
const PURCHASE_ORDER_STORE = "purchaseOrderData";
const inFlightGRNRequests = new Map();

const normalizeGRNResponse = (payload) => {
  if (Array.isArray(payload?.response)) {
    return payload.response;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.response && typeof payload.response === "object") {
    return [payload.response];
  }

  if (payload && typeof payload === "object" && payload.grn_token) {
    return [payload];
  }

  return [];
};

const normalizeUsersResponse = (payload) => {
  if (Array.isArray(payload?.response)) {
    return payload.response;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
};

const normalizePurchaseOrders = (payload) => {
  if (Array.isArray(payload?.response)) {
    return payload.response;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
};

const normalizeInvoicesResponse = (payload) => {
  if (Array.isArray(payload?.invoices)) {
    return payload.invoices;
  }

  if (Array.isArray(payload?.results?.invoices)) {
    return payload.results.invoices;
  }

  if (Array.isArray(payload?.response?.invoices)) {
    return payload.response.invoices;
  }

  return [];
};

const sortByCreatedAtDesc = (rows = []) =>
  [...rows].sort((a, b) => {
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();
    return bTime - aTime;
  });

const filterSyncedRows = (rows = []) =>
  rows.filter((row) => row?.sync_status !== "pending");

const buildInvoiceMap = (purchaseOrders = []) =>
  purchaseOrders.reduce((acc, po) => {
    if (po?.purchase_order_no) {
      acc[po.purchase_order_no] = po.invoice_number || null;
    }
    return acc;
  }, {});

const mapInvoiceNumber = (grnList = [], invoiceMap = {}) =>
  grnList
    .map((grn) => ({
      grn_token: grn.grn_token || "",
      grn_number: grn.grn_number ?? "",
      vendor: grn.vendor || "",
      purchase_order_no: grn.purchase_order_no || "",
      invoice_number: invoiceMap[grn.purchase_order_no] || null,
      receiver_name: grn.receiver_name || "",
      receiver_mail: grn.receiver_mail || "",
      receiver_department: grn.receiver_department || "",
      item_name: grn.item_name || "",
      item_code: grn.item_code || "",
      description: grn.description || "",
      quantity_ordered: grn.quantity_ordered ?? null,
      quantity_received: grn.quantity_received ?? null,
      uom: grn.uom || "",
      unit_price: grn.unit_price ?? null,
      total_price: grn.total_price ?? null,
      remarks: grn.remarks || "",
      created_at: grn.created_at || new Date().toISOString(),
      overall_approval_status: grn.overall_approval_status || "",
      approvals: Array.isArray(grn.approvals) ? grn.approvals : [],
      sync_status: "synced",
      offline_batch_id: null,
    }))
    .filter((grn) => grn.grn_token);

const normalizeLegacyGRNRows = (rows = []) =>
  rows
    .map((row) => row?.data || row)
    .filter(Boolean)
    .map((grn) => ({
      grn_token: grn.grn_token || grn.token || "",
      grn_number: grn.grn_number ?? "",
      vendor: grn.vendor || "",
      purchase_order_no: grn.purchase_order_no || "",
      invoice_number: grn.invoice_number ?? null,
      receiver_name: grn.receiver_name || "",
      receiver_mail: grn.receiver_mail || "",
      receiver_department: grn.receiver_department || "",
      item_name: grn.item_name || "",
      item_code: grn.item_code || "",
      description: grn.description || "",
      quantity_ordered: grn.quantity_ordered ?? null,
      quantity_received: grn.quantity_received ?? null,
      uom: grn.uom || "",
      unit_price: grn.unit_price ?? null,
      total_price: grn.total_price ?? null,
      remarks: grn.remarks || "",
      created_at: grn.created_at || new Date().toISOString(),
      overall_approval_status: grn.overall_approval_status || "",
      approvals: Array.isArray(grn.approvals) ? grn.approvals : [],
      sync_status: grn.sync_status || "synced",
      offline_batch_id: grn.offline_batch_id || null,
    }))
    .filter((grn) => grn.grn_token);

const normalizeInvoiceRecord = (invoice) => ({
  invoice_number: invoice.invoice_number || "",
  invoice_date: invoice.invoice_date || "",
  pi_po_number: invoice.pi_po_number || "",
  grand_total: invoice.grand_total ?? null,
  currency: invoice.currency || "",
  items: Array.isArray(invoice.items) ? invoice.items : [],
  synced_at: Date.now(),
});

export class GRNOfflineRepository {
  async fetchGRNFromAPI(authToken, userToken) {
    const requestKey = `${authToken || ""}:${userToken || ""}`;

    if (inFlightGRNRequests.has(requestKey)) {
      return inFlightGRNRequests.get(requestKey);
    }

    const request = (async () => {
      const [grnResponse, purchaseOrderResponse] = await Promise.all([
        axios.post(
          API_ENDPOINT.get_saved_grn,
          { token: userToken },
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        axios.post(
          API_ENDPOINT.get_purchase_order,
          { user_token: userToken },
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
      ]);

      const grnList = normalizeGRNResponse(grnResponse?.data);
      const purchaseOrders = normalizePurchaseOrders(purchaseOrderResponse?.data);
      const invoiceMap = buildInvoiceMap(purchaseOrders);
      const mergedGRNList = mapInvoiceNumber(grnList, invoiceMap);

      await this.savePurchaseOrdersToIndexedDB(purchaseOrders);
      await this.saveGRNToIndexedDB(mergedGRNList);

      return mergedGRNList;
    })();

    inFlightGRNRequests.set(requestKey, request);

    try {
      return await request;
    } finally {
      inFlightGRNRequests.delete(requestKey);
    }
  }

  async fetchUsers(authToken, buyerToken) {
    const response = await axios.post(
      API_ENDPOINT.all_user_list,
      { buyer_token: buyerToken },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );

    const users = normalizeUsersResponse(response?.data);
    await this.saveUsersToIndexedDB(users);
    return users;
  }

  async fetchInvoicesFromAPI(authToken, orgToken) {
    const response = await axios.post(
      API_ENDPOINT.get_invoices_by_org_token,
      { org_token: orgToken },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );

    const invoices = normalizeInvoicesResponse(response?.data);
    await this.saveInvoicesToIndexedDB(invoices);
    return invoices;
  }

  async fetchInvoices(authToken, orgToken) {
    return this.fetchInvoicesFromAPI(authToken, orgToken);
  }

  async fetchPurchaseOrders(authToken, userToken) {
    const response = await axios.post(
      API_ENDPOINT.get_purchase_order,
      { user_token: userToken },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );

    const purchaseOrders = normalizePurchaseOrders(response?.data);
    await this.savePurchaseOrdersToIndexedDB(purchaseOrders);
    return purchaseOrders;
  }

  async saveUsersToIndexedDB(users = []) {
    const rows = users.filter((user) => user?.token);

    await db.transaction("rw", db[USERS_STORE], async () => {
      await db[USERS_STORE].clear();
      if (!rows.length) {
        return;
      }

      await db[USERS_STORE].bulkPut(rows);
    });

    return rows;
  }

  async saveInvoicesToIndexedDB(invoices = []) {
    const rows = invoices
      .map(normalizeInvoiceRecord)
      .filter((invoice) => invoice.invoice_number);

    await db.transaction("rw", db[INVOICE_STORE], async () => {
      await db[INVOICE_STORE].clear();
      if (!rows.length) {
        return;
      }

      await db[INVOICE_STORE].bulkPut(rows);
    });

    return rows;
  }

  async savePurchaseOrdersToIndexedDB(purchaseOrders = []) {
    const rows = purchaseOrders.filter((po) => po?.purchase_order_no);

    await db.transaction("rw", db[PURCHASE_ORDER_STORE], async () => {
      await db[PURCHASE_ORDER_STORE].clear();

      if (!rows.length) {
        return;
      }

      await db[PURCHASE_ORDER_STORE].bulkPut(
        rows.map((po, index) => ({
          id: po.purchase_order_id || index + 1,
          purchase_order_id: po.purchase_order_id || index + 1,
          purchase_order_no: po.purchase_order_no,
          data: po,
          lastSynced: Date.now(),
        }))
      );
    });

    return rows;
  }

  async saveGRNToIndexedDB(grnList = []) {
    const apiRows = grnList.filter((grn) => grn?.grn_token);
    const pendingRows = await this.getPendingGRNRows();
    const pendingByToken = new Map(
      pendingRows.map((row) => [row.grn_token, row])
    );

    const mergedRows = [
      ...apiRows.filter((row) => !pendingByToken.has(row.grn_token)),
      ...pendingRows,
    ];

    await db.transaction("rw", db[GRN_STORE], db[LEGACY_GRN_STORE], async () => {
      await db[GRN_STORE].clear();
      await db[LEGACY_GRN_STORE].clear();

      if (!mergedRows.length) {
        return;
      }

      await db[GRN_STORE].bulkPut(mergedRows);
      await db[LEGACY_GRN_STORE].bulkPut(
        mergedRows.map((grn) => ({
          token: grn.grn_token,
          grn_token: grn.grn_token,
          purchase_order_no: grn.purchase_order_no || "",
          data: grn,
          lastSynced: Date.now(),
        }))
      );
    });

    return mergedRows;
  }

  async getGRNFromIndexedDB() {
    const currentRows = await db[GRN_STORE].toArray();

    if (currentRows.length > 0) {
      return sortByCreatedAtDesc(filterSyncedRows(currentRows));
    }

    const legacyRows = await db[LEGACY_GRN_STORE].toArray();
    return sortByCreatedAtDesc(
      filterSyncedRows(normalizeLegacyGRNRows(legacyRows))
    );
  }

  async getUsersFromIndexedDB() {
    return db[USERS_STORE].toArray();
  }

  async getInvoicesFromIndexedDB() {
    return db[INVOICE_STORE].toArray();
  }

  async getInvoices(authToken, orgToken) {
    if (!shouldMakeAPICall(authToken, orgToken)) {
      return this.getInvoicesFromIndexedDB();
    }

    try {
      return await this.fetchInvoicesFromAPI(authToken, orgToken);
    } catch (error) {
      console.error("GRN invoice preload failed:", error);
      return this.getInvoicesFromIndexedDB();
    }
  }

  async getPurchaseOrdersFromIndexedDB() {
    const rows = await db[PURCHASE_ORDER_STORE].toArray();
    return rows.map((row) => row.data || row);
  }

  async getInvoiceItems(invoiceNumber) {
    if (!invoiceNumber) {
      return [];
    }

    const invoice = await db[INVOICE_STORE].get(invoiceNumber);
    return Array.isArray(invoice?.items) ? invoice.items : [];
  }

  async getInvoiceByPONumber(poNumber) {
    if (!poNumber) {
      return null;
    }

    const invoices = await db[INVOICE_STORE].toArray();
    return (
      invoices.find((invoice) => invoice.pi_po_number === poNumber) || null
    );
  }

  async getCreateSupportData(authToken, userToken) {
    if (shouldMakeAPICall(authToken, userToken)) {
      try {
        const [users, purchaseOrders, invoices] = await Promise.all([
          this.fetchUsers(authToken, userToken),
          this.fetchPurchaseOrders(authToken, userToken),
          this.fetchInvoicesFromAPI(authToken, userToken),
        ]);

        return { users, invoices, purchaseOrders };
      } catch (error) {
        console.error("GRN create support sync failed:", error);
      }
    }

    const [users, invoices, purchaseOrders] = await Promise.all([
      this.getUsersFromIndexedDB(),
      this.getInvoicesFromIndexedDB(),
      this.getPurchaseOrdersFromIndexedDB(),
    ]);

    return { users, invoices, purchaseOrders };
  }

  async getPendingGRNRows() {
    const rows = await db[GRN_STORE]
      .where("sync_status")
      .equals("pending")
      .toArray();

    return rows;
  }

  async getGRNData(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return this.getGRNFromIndexedDB();
    }

    try {
      return await this.fetchGRNFromAPI(authToken, userToken);
    } catch (error) {
      console.error("GRN offline sync failed:", error);
      return this.getGRNFromIndexedDB();
    }
  }

  async createOfflineGRN(grnData = [], userToken) {
    const batchId = `offline-grn-${Date.now()}`;
    const grnNumber = `OFFLINE-${Date.now()}`;
    const createdAt = new Date().toISOString();
    const rows = (Array.isArray(grnData) ? grnData : [])
      .map((item, index) => ({
        grn_token: `${batchId}-${index + 1}`,
        grn_number: grnNumber,
        vendor: item.supplier_name || "",
        purchase_order_no: item.poNo || "",
        invoice_number: item.invoiceNo || null,
        delivery_date: item.delivery_date || item.deliveryDate || null,
        receiver_name: item.received_by || "",
        receiver_mail: item.received_email || "",
        receiver_department: item.department || "",
        item_name: item.item_name || "",
        item_code: item.item_code || "",
        description: item.description || "",
        quantity_ordered: item.q_ordered ?? null,
        quantity_received: item.q_received ?? null,
        uom: item.uom || "",
        unit_price: item.unit_price ?? null,
        total_price:
          item.total_price ??
          (Number(item.q_received || 0) * Number(item.unit_price || 0)),
        remarks: item.remarks || "",
        created_at: createdAt,
        overall_approval_status: "pending",
        approvals: [],
        sync_status: "pending",
        offline_batch_id: batchId,
        _sync_payload: {
          user_token: item.user_token || userToken,
          org_token: item.org_token || userToken,
          poNo: item.poNo || "",
          supplier_name: item.supplier_name || "",
          invoiceNo: item.invoiceNo || "",
          delivery_date: item.delivery_date || item.deliveryDate || null,
          received_by: item.received_by || "",
          received_email: item.received_email || "",
          department: item.department || "",
          item_name: item.item_name || "",
          item_code: item.item_code || "",
          description: item.description || "",
          q_ordered: item.q_ordered ?? null,
          q_received: item.q_received ?? null,
          uom: item.uom || "",
          unit_price: item.unit_price ?? null,
          remarks: item.remarks || "",
        },
      }))
      .filter((row) => row.grn_token);

    const existingRows = await db[GRN_STORE].toArray();
    await db.transaction("rw", db[GRN_STORE], db[LEGACY_GRN_STORE], async () => {
      await db[GRN_STORE].bulkPut([...rows, ...existingRows]);
      await db[LEGACY_GRN_STORE].bulkPut(
        [...rows, ...existingRows].map((grn) => ({
          token: grn.grn_token,
          grn_token: grn.grn_token,
          purchase_order_no: grn.purchase_order_no || "",
          data: grn,
          lastSynced: Date.now(),
        }))
      );
    });

    return rows;
  }

  async createGRNOnline(authToken, grnData = [], orgToken = "") {
    const res = await axios.post(API_ENDPOINT.ins_grn_data, grnData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    const extractGrnNumbers = (payload) => {
      if (!payload) return [];
      if (typeof payload === "string" || typeof payload === "number") {
        return [payload];
      }

      const direct =
        payload.grn_number ??
        payload.grnNo ??
        payload.grn_no ??
        payload.grnNumber ??
        payload.grn?.grn_no ??
        payload.grn?.grn_number;
      if (direct) return [direct];

      const grnDetails = Array.isArray(payload?.grn_details)
        ? payload.grn_details
        : [];
      if (grnDetails.length > 0) {
        return grnDetails
          .map((row) => row?.grn_number)
          .filter(Boolean);
      }

      const arr = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : null;
      if (!arr || arr.length === 0) return [];

      return arr
        .map((row) => {
          const v =
            row?.grn_number ??
            row?.grnNo ??
            row?.grn_no ??
            row?.grnNumber ??
            row?.grn?.grn_no ??
            row?.grn?.grn_number ??
            null;
          return v;
        })
        .filter(Boolean);
    };

    // If ins_grn_data returns GRN number(s), trigger asset creation right away.
    // Otherwise, the caller should trigger it after fetching saved GRNs.
    try {
      const resolvedOrgToken =
        orgToken ||
        grnData?.[0]?.org_token ||
        grnData?.[0]?.buyer_token ||
        grnData?.[0]?.user_token ||
        "";

      const grnNumbers = Array.from(new Set(extractGrnNumbers(res?.data)));
      for (const grnNumber of grnNumbers) {
        if (!resolvedOrgToken || !grnNumber) continue;
        await axios.post(
          API_ENDPOINT.create_asset_register_after_grn,
          { grn_number: grnNumber, org_token: resolvedOrgToken },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
      }
    } catch (error) {
      console.error("Asset register creation after GRN failed:", error);
    }

    return res;
  }

  async syncOfflineGRN(authToken, userToken) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      return [];
    }

    const pendingRows = await this.getPendingGRNRows();
    const grouped = pendingRows.reduce((acc, row) => {
      const key = row.offline_batch_id || row.grn_number || row.grn_token;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(row);
      return acc;
    }, {});

    const syncedBatchKeys = [];

    for (const [batchKey, rows] of Object.entries(grouped)) {
      try {
        const payload = rows.map((row) => ({
          ...row._sync_payload,
          user_token: row?._sync_payload?.user_token || userToken,
        }));

        await this.createGRNOnline(authToken, payload, userToken);

        await db.transaction("rw", db[GRN_STORE], db[LEGACY_GRN_STORE], async () => {
          for (const row of rows) {
            await db[GRN_STORE].delete(row.grn_token);
            const legacy = await db[LEGACY_GRN_STORE]
              .where("grn_token")
              .equals(row.grn_token)
              .first();
            if (legacy?.id) {
              await db[LEGACY_GRN_STORE].delete(legacy.id);
            }
          }
        });

        syncedBatchKeys.push(batchKey);
      } catch (error) {
        console.error("Offline GRN sync failed:", {
          batchKey,
          error,
          payloadPreview: rows.map((row) => row?._sync_payload),
        });
      }
    }

    if (syncedBatchKeys.length > 0) {
      await this.fetchGRNFromAPI(authToken, userToken);
    }

    return syncedBatchKeys;
  }
}

export const grnOfflineRepository = new GRNOfflineRepository();
export const fetchGRNFromAPI = (...args) =>
  grnOfflineRepository.fetchGRNFromAPI(...args);
export const fetchUsers = (...args) => grnOfflineRepository.fetchUsers(...args);
export const fetchInvoices = (...args) =>
  grnOfflineRepository.fetchInvoices(...args);
export const fetchInvoicesFromAPI = (...args) =>
  grnOfflineRepository.fetchInvoicesFromAPI(...args);
export const saveGRNToIndexedDB = (...args) =>
  grnOfflineRepository.saveGRNToIndexedDB(...args);
export const getGRNFromIndexedDB = (...args) =>
  grnOfflineRepository.getGRNFromIndexedDB(...args);
export const getInvoicesFromIndexedDB = (...args) =>
  grnOfflineRepository.getInvoicesFromIndexedDB(...args);
export const getInvoices = (...args) => grnOfflineRepository.getInvoices(...args);
export const createOfflineGRN = (...args) =>
  grnOfflineRepository.createOfflineGRN(...args);
export const syncOfflineGRN = (...args) =>
  grnOfflineRepository.syncOfflineGRN(...args);
export const getGRNData = (...args) => grnOfflineRepository.getGRNData(...args);
